import React, { useState, useRef, useEffect } from 'react';
import { FiGlobe, FiMenu, FiX } from 'react-icons/fi';

/** Read the language Google Translate stored in its cookie (/en/sv → 'sv') */
function getActiveGoogleLang(): string {
  try {
    const match = document.cookie.match(/googtrans=\/[a-z-]+\/([a-z-]+)/);
    if (match && match[1] && match[1] !== 'en') return match[1];
  } catch {}
  return localStorage.getItem('siteLang') || 'en';
}

export default function Header() {
  const [lang, setLang] = useState<string>(() => getActiveGoogleLang());
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const langs = [
    { code: 'en', label: 'EN', display: 'English' },
    { code: 'sv', label: 'SV', display: 'Svenska' },
    { code: 'es', label: 'ES', display: 'Español' },
    { code: 'de', label: 'DE', display: 'Deutsch' },
  ];

  const changeLanguage = (code: string) => {
    setLang(code);
    setOpen(false);
    localStorage.setItem('siteLang', code);
    // Write the googtrans cookie so it survives page navigation
    const domain = window.location.hostname;
    if (code === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      localStorage.removeItem('siteLang');
    } else {
      document.cookie = `googtrans=/en/${code}; path=/`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${domain}`;
    }

    const loadGoogleTranslate = (window as any).loadGoogleTranslate;
    if (code !== 'en' && typeof loadGoogleTranslate === 'function') {
      loadGoogleTranslate().then(() => {
        const applyGoogleTranslateLanguage = (window as any).applyGoogleTranslateLanguage;
        if (typeof applyGoogleTranslateLanguage === 'function') {
          applyGoogleTranslateLanguage(code);
        }
      });
      return;
    }

    setTimeout(() => {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      if (combo) {
        combo.value = code;
        combo.dispatchEvent(new Event('change'));
      }
    }, 150);
  };

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    if (window.location.pathname === to) return;
    window.history.pushState({}, '', to);
    window.dispatchEvent(new Event('app:navigate'));
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[#d5e3ed] bg-white/78 backdrop-blur-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a
            href="/"
            onClick={(e) => navigateTo(e, '/')}
            className="text-xl font-extrabold tracking-tight"
          >
            <span className="text-[var(--brand-ink)]">learnai</span>
            <span className="text-[var(--brand-ink)]">care.com</span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            <a
              href="/"
              onClick={(e) => navigateTo(e, '/')}
              className="relative text-sm font-semibold text-[#4a6077] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:text-[#345b49] hover:after:w-full after:bg-[#0f8b8d] after:transition-all"
            >
              HOME
            </a>
            <a
              href="/faqs"
              onClick={(e) => navigateTo(e, '/faqs')}
              className="relative text-sm font-semibold text-[#4a6077] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:text-[#345b49] hover:after:w-full after:bg-[#0f8b8d] after:transition-all"
            >
              FAQS
            </a>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-ink)] transition-colors hover:text-[var(--brand-accent)]"
                aria-haspopup="true"
                aria-expanded={open}
                translate="no"
              >
                <FiGlobe className="w-4 h-4 text-[var(--brand-accent)]" />
                <span>{langs.find(l => l.code === lang)?.label ?? 'EN'}</span>
              </button>

              {open && (
                <div className="absolute right-0 mt-3 w-44 rounded-xl border border-[#e6e5de] bg-white py-1 shadow-xl z-50">
                  {langs.map(l => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        lang === l.code ? 'bg-[var(--brand-accent-soft)] text-[var(--brand-accent)] font-semibold' : 'text-[var(--text-muted)] hover:bg-[var(--surface-0)]'
                      }`}
                      translate="no"
                    >
                      {l.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <button onClick={() => setMobileOpen(v => !v)} className="md:hidden p-2 text-[var(--text-muted)] hover:text-[var(--brand-accent)]">
            {mobileOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <nav className="md:hidden pb-4 pt-2 space-y-2 border-t border-[#e4edf3]">
            <a
              href="/"
              onClick={(e) => navigateTo(e, '/')}
              className="block rounded-lg px-3 py-2 text-sm font-semibold text-[#0f2744] hover:bg-[#345b49]"
            >
              HOME
            </a>
            <a
              href="/faqs.html"
              onClick={(e) => navigateTo(e, '/faqs.html')}
              className="block rounded-lg px-3 py-2 text-sm font-semibold text-[#0f2744] hover:bg-[#345b49]"
            >
              FAQS
            </a>
            <div className="flex gap-2 px-3 pt-1 flex-wrap">
              {langs.map(l => (
                <button key={l.code} onClick={() => { changeLanguage(l.code); setMobileOpen(false); }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    lang === l.code ? 'bg-[var(--brand-accent)] text-white border-[var(--brand-accent)]' : 'border-[#e6e5de] text-[var(--brand-ink)] hover:border-[var(--brand-accent)]'
                  }`}
                  translate="no"
                >{l.display}</button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
